import { useState } from "react";
import { FiEye, FiEyeOff, FiCheck } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import http from "../services/http";
import "../scss/register.scss";

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const requestedPath = typeof location.state?.from === "string" && location.state.from.startsWith("/")
        ? location.state.from
        : "/";
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        password: "",
        confirmPassword: "",
        whatsapp: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState("");

    const getPasswordCriteria = (pwd: string) => {
        return {
            hasCapital: /[A-Z]/.test(pwd),
            hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
            isLongEnough: pwd.length >= 6,
        };
    };

    const validatePassword = (pwd: string): boolean => {
        const { hasCapital, hasSymbol, isLongEnough } = getPasswordCriteria(pwd);
        return hasCapital && hasSymbol && isLongEnough;
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = "Nama lengkap harus diisi";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email harus diisi";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Email tidak valid";
        }

        if (!formData.password) {
            newErrors.password = "Password harus diisi";
        } else if (!validatePassword(formData.password)) {
            newErrors.password = "Password minimal 6 karakter, harus ada huruf kapital dan simbol";
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Konfirmasi password harus diisi";
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Password tidak cocok";
        }

        if (!formData.whatsapp.trim()) {
            newErrors.whatsapp = "Nomor WhatsApp harus diisi";
        } else if (!/^08[0-9]{7,10}$/.test(formData.whatsapp.replace(/\s/g, ""))) {
            newErrors.whatsapp = "Nomor WhatsApp harus dimulai dengan 08";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error saat user mulai mengetik
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setApiError("");

        try {
            const dataToSubmit = {
                fullname: formData.fullName,
                email: formData.email,
                password: formData.password,
                phone: formData.whatsapp,
            };

            console.log("Data to submit:", dataToSubmit);
            const response = await http.post("auth/register", dataToSubmit);
            
            console.log("Registration successful:", response.data);
            // Redirect to login page setelah register berhasil
            setTimeout(() => {
                navigate("/login", {
                    replace: true,
                    state: { from: requestedPath },
                });
            }, 1500);
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || "Pendaftaran gagal. Silakan coba lagi.";
            setApiError(errorMessage);
            console.error("Registration error:", error);
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = 
        formData.fullName.trim() &&
        formData.email.trim() &&
        formData.password &&
        formData.confirmPassword &&
        formData.whatsapp.trim() &&
        !Object.values(errors).some(err => err);

    return (
        <div className="register-container">
            <div className="register-card">
                <div className="register-header">
                    <h1>Daftar Sekarang</h1>
                    <p>Bergabunglah dengan kami dan mulai karir Anda</p>
                </div>

                <form onSubmit={handleSubmit} className="register-form">
                    {apiError && (
                        <div className="alert alert-error">
                            {apiError}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="fullName">Nama Lengkap *</label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Masukkan nama lengkap"
                            className={`form-input ${errors.fullName ? "error" : ""}`}
                        />
                        {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Masukkan email"
                            className={`form-input ${errors.email ? "error" : ""}`}
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <div className="password-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Min 6 karakter, harus ada huruf besar & simbol"
                                className={`form-input ${errors.password ? "error" : ""}`}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                        
                        {formData.password && (
                            <div className="password-criteria">
                                <div className={`criteria-item ${getPasswordCriteria(formData.password).hasCapital ? "met" : ""}`}>
                                    <FiCheck className="criteria-icon" />
                                    <span>Huruf besar (A-Z)</span>
                                </div>
                                <div className={`criteria-item ${getPasswordCriteria(formData.password).hasSymbol ? "met" : ""}`}>
                                    <FiCheck className="criteria-icon" />
                                    <span>Simbol (!@#$%^&*)</span>
                                </div>
                                <div className={`criteria-item ${getPasswordCriteria(formData.password).isLongEnough ? "met" : ""}`}>
                                    <FiCheck className="criteria-icon" />
                                    <span>Minimal 6 karakter</span>
                                </div>
                            </div>
                        )}
                        
                        {errors.password && <span className="error-message">{errors.password}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Konfirmasi Password *</label>
                        <div className="password-wrapper">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Ulangi password"
                                className={`form-input ${errors.confirmPassword ? "error" : ""}`}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                        {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="whatsapp">Nomor WhatsApp *</label>
                        <input
                            type="tel"
                            id="whatsapp"
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleChange}
                            placeholder="08xx"
                            className={`form-input ${errors.whatsapp ? "error" : ""}`}
                        />
                        {errors.whatsapp && <span className="error-message">{errors.whatsapp}</span>}
                    </div>

                    <button
                        type="submit"
                        className={`submit-button ${isFormValid && !loading ? "active" : "disabled"}`}
                        disabled={!isFormValid || loading}
                    >
                        {loading ? "Sedang Mendaftar..." : "Daftar"}
                    </button>

                    <p className="login-link">
                        Sudah punya akun?{" "}
                        <Link to="/login" state={{ from: requestedPath }}>
                            Masuk di sini
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Register;
